import { create } from "zustand";
import { dealApi, type GoogleCategoryTree } from "~/api/deal";
import { reportUnexpectedError } from "~/api/client";
import { GOOGLE_ROOT_CATEGORY_CONFIG } from "../category";

const FILTERED_KEYWORDS = ["adult", "mature", "erotic", "porn", "sex"];

const shouldFilterCategory = (category: GoogleCategoryTree): boolean => {
  if (!category.category) return false;
  const categoryLower = category.category.toLowerCase();
  return FILTERED_KEYWORDS.some(keyword => categoryLower.includes(keyword));
};

const filterCategories = (categories: GoogleCategoryTree[]): GoogleCategoryTree[] => {
  return categories.filter(category => !shouldFilterCategory(category));
};

export interface RootGoogleCategoryTree extends GoogleCategoryTree {
  tabId: string;
}
export interface GoogleCategoriesState {
  rootCategories: RootGoogleCategoryTree[];

  subcategoriesCache: Map<string, GoogleCategoryTree[]>;

  selectedCategoryId: string | null;

  isLoading: boolean;
  error: string | null;

  setRootCategories: () => Promise<void>;
  setSelectedCategory: (categoryId: string) => void;
  getSubcategories: (parentId: string) => GoogleCategoryTree[] | undefined;
  fetchSubcategoriesIfNeeded: (parentId: string) => Promise<GoogleCategoryTree[]>;
  reset: () => void;

  setNotLoading: () => void;
  trySetLoading: () => boolean;
}

export const useGoogleCategoriesStore = create<GoogleCategoriesState>((set, get) => ({
  rootCategories: [],
  subcategoriesCache: new Map(),
  selectedCategoryId: null,

  isLoading: false,
  error: null,

  trySetLoading: () => {
    const state = get();
    if (state.isLoading) {
      return false;
    }
    set({ isLoading: true, error: null });
    return true;
  },

  setNotLoading: () => {
    set({ isLoading: false });
  },

  setRootCategories: async () => {
    const categoryConfig = GOOGLE_ROOT_CATEGORY_CONFIG;
    const categories: RootGoogleCategoryTree[] = [];
    for (const category of categoryConfig) {
      categories.push({
        categoryId: category.entrypoint,
        category: category.label,
        tabId: category.id,
      });
    }
    set({
      rootCategories: categories,
      selectedCategoryId: categories[0]?.categoryId || null,
    });
  },

  setSelectedCategory: (categoryId: string) => {
    set({ selectedCategoryId: categoryId });
  },

  getSubcategories: (parentId: string) => {
    return get().subcategoriesCache.get(parentId);
  },

  fetchSubcategoriesIfNeeded: async (parentId: string) => {
    // Check if already in cache
    const cached = get().subcategoriesCache.get(parentId);
    if (cached !== undefined) {
      return cached;
    }

    // Not in cache, fetch it
    try {
      const response = await dealApi.getGoogleCategories({ parentId });
      const allSubcategories = response.categories || [];
      const subcategories = filterCategories(allSubcategories);

      // Update cache
      const newCache = new Map(get().subcategoriesCache);
      newCache.set(parentId, subcategories);
      set({ subcategoriesCache: newCache });

      return subcategories;
    } catch (error) {
      reportUnexpectedError(error);
      // Cache empty array to avoid repeated failed requests
      const newCache = new Map(get().subcategoriesCache);
      newCache.set(parentId, []);
      set({ subcategoriesCache: newCache });
      return [];
    }
  },

  reset: () => {
    set({
      rootCategories: [],
      subcategoriesCache: new Map(),
      selectedCategoryId: null,
      isLoading: false,
      error: null,
    });
  },
}));
